import { api } from "../../../lib/api";
import {
  ContactMessage,
  ContactMessagesResponse,
  ContactStatsResponse,
  UpdateStatusPayload,
  BulkUpdatePayload,
  ContactQueryParams,
} from "./contact.types";

class ContactService {
  /**
   * Get all contact messages with filters
   * @param {ContactQueryParams} params
   * @returns {Promise<ContactMessagesResponse>}
   */
  async getMessages(params) {
    try {
      const response = await api.get("contact/admin/contact/messages", { params });
      if (response.data.status && response.data.data) {
        const { messages, pagination, stats } = response.data.data;

        const contactMessages = messages.map(
          (msg) =>
            new ContactMessage(
              msg.id,
              msg.name,
              msg.email,
              msg.message,
              msg.status,
              msg.ip_address,
              msg.user_agent,
              msg.created_at,
              msg.updated_at
            )
        );

        return new ContactMessagesResponse(
          true,
          contactMessages,
          pagination,
          stats
        );
      }

      return new ContactMessagesResponse(false, [], null, null);
    } catch (error) {
      const message =
        error.response?.data?.data?.error ||
        "Failed to fetch contact messages";
      throw new Error(message);
    }
  }

  /**
   * Get contact statistics
   * @returns {Promise<ContactStatsResponse>}
   */
  async getStats() {
    try {
      const response = await api.get("contact/admin/contact/stats");

      if (response.data.status && response.data.data) {
        return new ContactStatsResponse(true, response.data.data.stats);
      }

      return new ContactStatsResponse(false, null);
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to fetch statistics";
      throw new Error(message);
    }
  }

  /**
   * Update message status
   * @param {number} id
   * @param {UpdateStatusPayload} payload
   * @returns {Promise<{status: boolean, message: string, data: any}>}
   */
  async updateStatus(id, payload) {
    try {
      const response = await api.patch(
        `contact/admin/contact/messages/${id}/status`,
        payload
      );

      return {
        status: response.data.status,
        message: response.data.data.message,
        data: response.data.data.updated_message,
      };
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to update status";
      throw new Error(message);
    }
  }

  /**
   * Bulk update message status
   * @param {BulkUpdatePayload} payload
   * @returns {Promise<{status: boolean, message: string, count: number}>}
   */
  async bulkUpdateStatus(payload) {
    try {
      const response = await api.patch(
        "contact/admin/contact/messages/bulk/status",
        payload
      );

      return {
        status: response.data.status,
        message: response.data.data.message,
        count: response.data.data.updated_count,
      };
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to bulk update";
      throw new Error(message);
    }
  }

  /**
   * Delete a contact message
   * @param {number} id
   * @returns {Promise<{status: boolean, message: string}>}
   */
  async deleteMessage(id) {
    try {
      const response = await api.delete(`contact/admin/contact/messages/${id}`);

      return {
        status: response.data.status,
        message: response.data.data.message,
      };
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to delete message";
      throw new Error(message);
    }
  }

  /**
   * Bulk delete messages
   * @param {number[]} ids
   * @returns {Promise<{status: boolean, message: string, count: number}>}
   */
  async bulkDelete(ids) {
    try {
      const response = await api.delete("contact/admin/contact/messages/bulk/delete", {
        data: { ids },
      });

      return {
        status: response.data.status,
        message: response.data.data.message,
        count: response.data.data.deleted_count,
      };
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to bulk delete";
      throw new Error(message);
    }
  }
}

export const contactService = new ContactService();